import type { ActivityPayload, ActivityTransport, FlushOptions, SendResult } from './contracts';
import type { TrackerLogger } from './logger';
import { encodeActivityPayload } from './payload-encoding';

/** The ingestion service sends Retry-After in seconds. */
const retryAfterMsFrom = (value: string | null): number | null => {
  if (value === null || !/^\d{1,6}$/.test(value.trim())) return null;
  return Number(value.trim()) * 1_000;
};

export class BrowserTransport implements ActivityTransport {
  constructor(
    private readonly endpoint: string,
    private readonly logger: TrackerLogger
  ) {}

  async send(
    payload: ActivityPayload,
    options: FlushOptions = {}
  ): Promise<SendResult> {
    const encoded = encodeActivityPayload(payload);
    const url = `${this.endpoint}/activity`;
    const body = encoded.body;

    if (encoded.compression === 'identity') {
      this.logger.debug('encode_activity', {
        outcome: 'fallback',
        compression: encoded.compression
      });
    }

    if (options.preferBeacon && typeof navigator.sendBeacon === 'function' && typeof Blob === 'function') {
      try {
        const accepted = navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        if (accepted) return { status: 'sent', transport: 'beacon' };
      } catch (error) {
        this.logger.debug('send_beacon', {
          outcome: 'failed',
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }

    if (typeof fetch !== 'function') return { status: 'failed', reason: 'fetch is unavailable' };

    try {
      const response = await fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        credentials: 'omit',
        keepalive: true,
        mode: 'cors'
      });

      if (!response.ok) {
        const retryable = response.status === 408 || response.status === 425 || response.status === 429 || response.status >= 500;
        if (!retryable) return { status: 'dropped', reason: `http_${response.status}` };
        const retryAfterMs = retryAfterMsFrom(response.headers.get('Retry-After'));
        return retryAfterMs === null
          ? { status: 'failed', reason: `http_${response.status}` }
          : { status: 'failed', reason: `http_${response.status}`, retryAfterMs };
      }
      return { status: 'sent', transport: 'fetch' };
    } catch (error) {
      return { status: 'failed', reason: error instanceof Error ? error.message : String(error) };
    }
  }
}
